import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { AvatarChip, LOGO_URL } from "./AvatarChip";
import { NotificationBell } from "./NotificationBell";
import { MobileNavDrawer, MobileNavBurger } from "./MobileNavDrawer";

const NAV = [
  { id: "dashboard", label: "DASHBOARD", route: "/dashboard", authOnly: true },
  { id: "players", label: "PLAYERS", route: "/players" },
  { id: "leagues", label: "LEAGUES", route: "/leagues" },
  { id: "hqs", label: "TEAMS", route: "/hqs" },
  { id: "leaderboards", label: "RANKINGS", route: "/leaderboards" },
  { id: "lfg", label: "LFG", route: "/lfg" },
  { id: "wallet", label: "WALLET", route: "/wallet", authOnly: true, flag: "tips_enabled" },
  { id: "pricing", label: "PRICING", route: "/pricing" },
  { id: "contact", label: "CONTACT", route: "/contact" },
];

export function Navbar({ onSignIn, onOpenProfile, onOpenWhatsNew }) {
  const { user, logout, inboxCount } = useAuth();
  const { flags } = useFeatureFlags();
  const location = useLocation();
  const hasMail = (inboxCount || 0) > 0;
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <nav className="border-b border-[rgba(20,184,166,0.22)] bg-[rgba(31,31,37,0.85)] backdrop-blur-xl sticky top-0 z-40 page-z">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-home-link">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-display text-white tracking-[3px] neon-text">LEAGUE HUB</h1>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-7 text-[12px] font-semibold tracking-[2px] uppercase">
          {NAV.filter((n) => !n.authOnly || user).filter((n) => !n.flag || flags?.[n.flag]).map((n) => {
            const isActive = location.pathname === n.route;
            return (
              <Link
                key={n.id}
                to={n.route}
                data-testid={`nav-${n.id}`}
                className={`relative pb-1.5 transition-colors ${
                  isActive ? "text-white" : "text-white/70 hover:text-white"
                }`}
              >
                {n.id === "dashboard" && <i className="fas fa-house mr-1.5 opacity-90" />}
                {n.label}
                {isActive && (
                  <span className="absolute left-0 right-0 -bottom-0.5 h-[2px] bg-[#14b8a6] rounded-full shadow-[0_0_8px_rgba(20,184,166,0.7)]" />
                )}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          {!user ? (
            <>
              <button
                onClick={onSignIn}
                data-testid="navbar-discord-btn"
                className="hidden md:flex items-center gap-2 px-5 py-2.5 rounded-3xl border border-[#5865F2]/70 hover:border-white text-white font-medium"
              >
                <i className="fab fa-discord" />
                <span>Discord</span>
              </button>
              <button
                onClick={onSignIn}
                data-testid="navbar-google-btn"
                className="hidden md:flex items-center gap-2 px-5 py-2.5 rounded-3xl border border-white/40 hover:border-white text-white font-medium"
              >
                <i className="fab fa-google text-[#DB4437]" />
                <span>Google</span>
              </button>
              {/* SIGN IN on mobile is shown inside the drawer (with both
                  Discord + Google options); on lg+ we keep the inline
                  combo for the desktop layout untouched. */}
              <button
                onClick={onSignIn}
                data-testid="navbar-signin-btn"
                className="hidden md:inline-flex btn-neon px-5 py-2.5 rounded-3xl text-sm"
              >
                SIGN IN
              </button>
              <MobileNavBurger onClick={() => setDrawerOpen(true)} hasMail={false} />
            </>
          ) : (
            <div className="flex items-center gap-2">
              <NotificationBell />
              <button
                onClick={() => onOpenProfile?.("inbox")}
                data-testid="navbar-inbox-btn"
                title={hasMail ? `${inboxCount} new` : "Inbox"}
                className={`relative hidden md:inline-flex items-center gap-2 rounded-2xl px-3 py-2.5 text-xs font-bold tracking-widest transition-colors ${
                  hasMail
                    ? "bg-[rgba(20,184,166,0.10)] border border-[rgba(20,184,166,0.45)] text-white"
                    : "btn-ghost"
                }`}
              >
                <i className={`fas ${hasMail ? "fa-envelope" : "fa-inbox"} text-[#14b8a6]`} />
                INBOX
                {hasMail && (
                  <span
                    data-testid="navbar-inbox-badge"
                    className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 rounded-full bg-[#111113] border border-[rgba(20,184,166,0.55)] text-[#14b8a6] text-[10px] font-bold flex items-center justify-center"
                  >
                    {inboxCount > 99 ? "99+" : inboxCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => onOpenProfile?.()}
                data-testid="navbar-profile-btn"
                className="relative hidden md:flex items-center bg-black/60 border border-[#1e2a3a]/50 rounded-3xl pl-2 pr-4 py-1.5 gap-3 hover:border-[#14b8a6] transition-colors"
              >
                <AvatarChip avatarId={user.avatar_id} pfpUrl={user.pfp_url} size={36} />
                <div className="text-left">
                  <p className="text-sm font-bold leading-tight">{user.name}</p>
                  <p className="text-[10px] text-[#14b8a6] tracking-widest">
                    {(user.user_type || "casual").toUpperCase()} • {(user.plan || "free").toUpperCase()}
                  </p>
                </div>
                {hasMail && (
                  <span
                    data-testid="navbar-profile-mail-dot"
                    aria-label="You have new mail"
                    title="You have new mail"
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#14b8a6] border-2 border-black flex items-center justify-center text-[9px] text-black shadow-[0_0_8px_rgba(20, 184, 166,0.8)] animate-glow-pulse"
                  >
                    <i className="fas fa-envelope" />
                  </span>
                )}
              </button>
              <button
                onClick={logout}
                data-testid="navbar-logout-btn"
                title="Logout"
                className="btn-ghost rounded-2xl px-3 py-2.5 hidden md:inline-flex"
              >
                <i className="fas fa-right-from-bracket" />
              </button>
              {/* Mobile: collapse the cluster of inbox + profile + logout
                  buttons behind a single burger. The drawer surfaces
                  every action at full size for finger-friendly taps. */}
              <MobileNavBurger onClick={() => setDrawerOpen(true)} hasMail={hasMail} />
            </div>
          )}
        </div>
      </div>
      <MobileNavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        navItems={NAV}
        onSignIn={onSignIn}
        onOpenProfile={onOpenProfile}
      />
    </nav>
  );
}

export default Navbar;
